import { PagedMessageResponse } from "app/api/types/PagedMessageResponse"
import { SelecaoComFiltro } from "app/components/selecao-com-filtro/SelecaoComFiltro"
import { Separador } from "app/components/separador/Separador"
import { CepPorEndereco } from "app/types/CepPorEndereco"
import { ICidade } from "app/types/ICidade"
import { IClassificacao } from "app/types/IClassificacao"
import { IEndereco } from "app/types/IEndereco"
import { IEstado } from "app/types/IEstado"
import { FormikErrors, useFormik } from "formik"
import globalMU from "globalMU"
import * as R from "ramda"
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Bairro } from "../../../../types/NovoEndereco"
import { InputCep } from "../../../inputs/InputCep"

const estiloBotoes = require('../../../../estilos/botoes.module.scss').default
const estilosInputs = require("../../../../estilos/inputs.module.scss").default

export type NovoEnderecoProps = {
  bairros: Bairro[]
  estados: IEstado[]
  cidades: ICidade[]
  opcoesTipo: IClassificacao[]
  utilizarBairrosPreCadastrados: boolean
  numeroEBairroObrigatorio: boolean
  endereco: IEndereco
  dadosModalEndereco: PagedMessageResponse<CepPorEndereco>
  adicionandoEndereco: boolean
  onErrorValidacao: (erroNovoEndereco: FormikErrors<IEndereco>) => void
  onAdicionar: (endereco: IEndereco) => void
  onPesquisarCep: (cep: string) => void
  onListarBairros: (bairro: string) => void
  onListarCidadesPorEstado: (codigoEstado: number) => void
  pesquisaPorEndereco: () => void
  atualzaDadosASerSalvos: (endereco: IEndereco) => void
}

export const NovoEndereco = memo((props: NovoEnderecoProps) => {
  const [validado, setValidado] = useState(false)
  const [carregandoBairro, setCarregadoBairro] = useState(false)
  const [comboBairro, setComboBairro] = useState(props.endereco?.Bairro ?? "")
  const isMounted = useRef<boolean>(true)

  useEffect(() => () => { isMounted.current = false }, [])

  const formik = useFormik({
    initialValues: { ...props.endereco },
    onSubmit: (values, { }) => { },
    enableReinitialize: true,
    validate: (values) => {
      const mensagemObrigatorio = (campo) => `O campo "${campo}" é obrigatório.`

      return R.pickBy((val) => globalMU.isNotNullOrEmpty(val), {
        Classificacoes: (values.Classificacoes == null || (values.Classificacoes?.length <= 0 && !values.DescricaoClassificacoes)) ? mensagemObrigatorio("Tipo") : undefined,
        Cep: !values.Cep ? mensagemObrigatorio("Cep") : undefined,
        Numero: !values.Numero && props.numeroEBairroObrigatorio ? mensagemObrigatorio("Numero") : undefined,
        Logradouro: !values.Logradouro ? mensagemObrigatorio("Logradouro") : undefined,
        Bairro: !values.Bairro && props.numeroEBairroObrigatorio ? mensagemObrigatorio("Bairro") : undefined,
        CodigoEstado: !values.CodigoEstado && !values.SiglaUf ? mensagemObrigatorio("UF") : undefined,
        CodigoCidade: !values.CodigoCidade && !values.NomeCidade ? mensagemObrigatorio("Cidade") : undefined,
      } as FormikErrors<IEndereco>)
    },
  })

  useEffect(() => {
    if (isMounted.current) {
      props.atualzaDadosASerSalvos(formik.values)
    }
  }, [formik.values])

  useEffect(() => {
    if (isMounted.current) {
      setCarregadoBairro(false)
    }
  }, [props.bairros])

  useEffect(() => {
    if (isMounted.current) {
      setComboBairro(props.endereco?.Bairro)
    }
  }, [props.endereco?.Bairro])

  const handleTipoChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const disponibilidadesSelecionadas = Array.from(e.target.selectedOptions)?.map((o) => o.value)
    const nomeDisponibilidadesSelecionadas = props.opcoesTipo
      .filter((tipo) => disponibilidadesSelecionadas.includes(tipo.Codigo.toString()))
      .map(tipo => tipo.Nome)
      .toString()
    formik.setFieldValue("Classificacoes", disponibilidadesSelecionadas)
    formik.setFieldValue("DescricaoClassificacoes", nomeDisponibilidadesSelecionadas)
  }, [props.opcoesTipo])

  const handleUfChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const codigoEstado = e.target.value
    formik.setFieldValue("CodigoEstado", codigoEstado)
    formik.setFieldValue("SiglaUf", props.estados.find(estado => estado.Codigo.toString() === codigoEstado)?.Sigla)
    props.onListarCidadesPorEstado(Number(codigoEstado))
  }, [props.estados, props.onListarCidadesPorEstado])

  const handleCidadeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const codigoCidade = e.target.value
    formik.setFieldValue("CodigoCidade", codigoCidade)
    formik.setFieldValue("NomeCidade", props.cidades.find(cidade => cidade.Codigo.toString() === codigoCidade)?.Nome)
  }, [props.cidades])

  const handleAdicionarClick = useCallback(async () => {
    if (!isMounted.current) return
    const erros = await formik.validateForm(formik.values)
    if (Object.keys(erros).length === 0) {
      props.onAdicionar(formik.values)
    } else {
      props.onErrorValidacao(erros)
    }
    setValidado(true)
  }, [formik, props.onAdicionar, props.onErrorValidacao])

  const tipo = (
    <React.Fragment key="NovoEndereco_Tipo">
      <label className="_w-[160px] _text-right _font-normal" id="NovoEndereco_LabelTipo">
        Tipo <p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>
      </label>
      <div>
        <select
          className={`_w-[100px] _h-[112px] ${validado && formik.errors.Classificacoes != undefined && estilosInputs.inputComErro}`}
          multiple={true}
          name="Classificacoes"
          autoFocus
          id="NovoEndereco_SelectTipo"
          value={(formik.values.Classificacoes?.map(x => x.toString())) ?? ""}
          size={7}
          onChange={handleTipoChange}
        >
          {props.opcoesTipo?.map((tipo) => (
            <option key={`NovoEndereco_TipoOption_${tipo.Codigo}`} value={tipo.Codigo}>
              {tipo.Nome}
            </option>
          ))}
        </select>
      </div>
    </React.Fragment>
  )

  const cep = (
    <React.Fragment key="NovoEndereco_Cep">
      <label 
        className="_w-[160px] _text-right _font-normal" 
        id="NovoEndereco_LabelCep"
        title="Veja parâmetro:&#013;-Obter endereço a partir do CEP"
      >
        CEP <p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>
      </label>
      <div>
        <InputCep
          className={`_w-[100px] ${validado && formik.errors.Cep != undefined && estilosInputs.inputComErro}`}
          cep={formik.values.Cep}
          onCepModificado={(cep) => {
            formik.setFieldValue("Cep", cep)
            cep != null && props.onPesquisarCep(cep)
          }}
          emitirComMascara
          id="NovoEndereco_InputCep"
        />
      </div>
    </React.Fragment>
  )

  const buttonPesquisaCep = (
    <button
      className={`no-global-button ${estiloBotoes.botaoPrimario}`}
      disabled={formik.values.Cep?.length > 0}
      id="NovoEndereco_BotaoPesquisarCep"
      onClick={() => props.pesquisaPorEndereco()}
      key="NovoEndereco_BotaoPesquisarCep"
    >
      Pesquisar CEP
    </button>
  )

  const logradouro = (
    <React.Fragment key="NovoEndereco_Logradouro">
      <label 
        className="_w-[160px] _text-right _font-normal"
        id="NovoEndereco_LabelLogradouro"
        title="É possível pesquisar o CEP informando o nome da rua e clicando no botão 'Pesquisar CEP'"
      >
        Logradouro<p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>
      </label>
      <div>
        <input
          name="Logradouro"
          id="NovoEndereco_InputLogradouro"
          type="text"
          className={`_w-[280px] _mr-1 ${validado && formik.errors.Logradouro != undefined && estilosInputs.inputComErro}`}
          style={{ marginRight: "5px" }}
          maxLength={254}
          value={(formik.values.Logradouro) ?? ""}
          onChange={formik.handleChange}
        />
        {buttonPesquisaCep}
      </div>
    </React.Fragment>
  )

  const numero = (
    <React.Fragment key="NovoEndereco_Numero">
      <label className="_w-[160px] _text-right _font-normal" id="NovoEndereco_LabelNumero">
        Número {props.numeroEBairroObrigatorio && <p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>}
      </label>
      <div>
        <input
          name="Numero"
          id="NovoEndereco_InputNumero"
          type="text"
          className={`_w-[100px] ${validado && formik.errors.Numero != undefined && estilosInputs.inputComErro}`}
          maxLength={254}
          value={(formik.values.Numero) ?? ""}
          onChange={formik.handleChange}
        />
      </div>
    </React.Fragment>
  )

  const complemento = (
    <React.Fragment key="NovoEndereco_Complemento">
      <label className="_w-[160px] _text-right _font-normal" id="NovoEndereco_LabelComplemento">Complemento</label>
      <div>
        <input
          name="Complemento"
          id="NovoEndereco_InputComplemento"
          type="text"
          className="_w-[280px]"
          maxLength={254}
          value={(formik.values.Complemento) ?? ""}
          onChange={formik.handleChange}
        />
      </div>
    </React.Fragment>
  )

  const bairro = (
    <React.Fragment key="NovoEndereco_Bairro">
      <label 
        title="Utilizar no Orçamento bairros pré-cadastrados = Não" 
        className="_w-[160px] _text-right _font-normal"
        id="NovoEndereco_LabelBairro"
      >
        Bairro {props.numeroEBairroObrigatorio && <p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>}
      </label>
      {props.utilizarBairrosPreCadastrados ? (
        <SelecaoComFiltro
          classNameInput={`_w-[280px] ${validado && formik.errors.Bairro != undefined && estilosInputs.inputComErro}`}
          itens={carregandoBairro ? [{ chave: "", valor: "Buscando..." }] : (props.bairros ?? []).map(bairro => ({
            chave: bairro.Codigo.toString(),
            valor: bairro.Nome
          }))}
          onValorModificado={(bairro) => (setCarregadoBairro(true), formik.setFieldValue("Bairro", ""), setComboBairro(bairro), props.onListarBairros(bairro))}
          onItemSelecionado={item => {
            formik.setFieldValue("Bairro", (props?.bairros ?? []).find(b => b.Codigo.toString() == item.chave)?.Nome)
            setComboBairro((props?.bairros ?? []).find(b => b.Codigo.toString() == item.chave)?.Nome)
          }}
          itemSelecionado={{
            chave: (props?.bairros ?? []).find(b => b.Nome.toString() == formik.values.Bairro)?.Codigo.toString(),
            valor: comboBairro
          }}
          filtroManual={true}
          placeholder="Digite 1 ou mais caracteres"
          id="NovoEndereco_SelectBairro"
        />
      ) : (
        <input
          className={`_w-[280px] ${validado && formik.errors.Bairro != undefined && estilosInputs.inputComErro}`}
          name="Bairro"
          id="NovoEndereco_InputBairro"
          value={(formik.values?.Bairro) ?? ""}
          onChange={formik.handleChange}
        />
      )}
    </React.Fragment>
  )

  const uf = (
    <React.Fragment key="NovoEndereco_Uf">
      <label className="_w-[160px] _text-right _font-normal" id="NovoEndereco_LabelUf">
        U.F.<p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>
      </label>
      <div>
        <select
          className={`_w-[60px] ${validado && formik.errors.CodigoEstado != undefined && estilosInputs.inputComErro}`}
          multiple={false}
          value={formik.values?.CodigoEstado ?? ''}
          onChange={handleUfChange}
          id="NovoEndereco_SelectUf"
        >
          <option value="">Selecione</option>
          {props.estados?.map((value) => (
            <option
              key={`NovoEndereco_UfOption_${value.Codigo}`}
              value={value.Codigo}
            >
              {value.Sigla}
            </option>
          ))}
        </select>
      </div>
    </React.Fragment>
  )

  const cidade = (
    <React.Fragment key="NovoEndereco_Cidade">
      <label className="_w-[160px] _text-right _font-normal" id="NovoEndereco_LabelCidade">
        Cidade<p className="_inline-flex _ml-[5px] _mb-0 _font-bold _text-[#f25713]">*</p>
      </label>
      <div>
        <select
          multiple={false}
          className={`_w-[280px] ${validado && formik.errors.CodigoCidade != undefined && estilosInputs.inputComErro}`}
          value={formik.values?.CodigoCidade ?? ''}
          onChange={handleCidadeChange}
          id="NovoEndereco_SelectCidade"
        >
          <option value="">Selecione</option>
          {props.cidades != null && (
            props.cidades?.map((value) => (
              <option
                key={`NovoEndereco_CidadeOption_${value.Codigo}`}
                value={value.Codigo}
              >
                {value.Nome}
              </option>
            )))}
        </select>
      </div>
    </React.Fragment>
  )

  const referencia = (
    <React.Fragment key="NovoEndereco_Referencia">
      <label className="_w-[160px] _text-right _font-normal" id="NovoEndereco_LabelReferencia">Referência</label>
      <div>
        <input
          name="Referencia"
          id="NovoEndereco_InputReferencia"
          type="text"
          className="_w-[280px]"
          maxLength={254}
          value={(formik.values.Referencia) ?? ""}
          onChange={formik.handleChange}
        />
      </div>
    </React.Fragment>
  )

  const buttonAdicionar = (
    <button
      className={`no-global-button _mr-24 ${estiloBotoes.botaoPrimario}`}
      id="NovoEndereco_BotaoAdicionar"
      disabled={props.adicionandoEndereco}
      onClick={handleAdicionarClick}
      key="NovoEndereco_BotaoAdicionar"
    >
      {props.endereco?.Codigo == null ? "Adicionar" : "Alterar"}
    </button>
  )

  const formFields = useMemo(() => [
    tipo,
    cep,
    logradouro,
    numero,
    complemento,
    bairro,
    uf,
    cidade,
    referencia,
    buttonAdicionar
  ], [formik.values, validado, carregandoBairro, props.bairros, props.estados, props.cidades, props.opcoesTipo])

  return (
    <React.Fragment>
      <Separador />
      <div className={`_grid _grid-cols-[min-content_auto] _gap-y-2 _gap-x-3 _float-left _my-2`}>
        {formFields}
      </div>
    </React.Fragment>
  );
})